import { Box, Divider, Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { Text } from "@src/appV2/redesign/components/Text";
import { useIsRebrand } from "@src/appV2/redesign/theme/RebrandProvider";
import {
  type AccountSwitcherUser,
  type AdminSearchTabValue,
  type UserDetails,
  ADMIN_SEARCH_PLACEHOLDER,
  ADMIN_SEARCH_TAB_LABELS,
  ADMIN_SEARCH_TAB_OPTIONS,
} from "../../Shared/AccountSwitcher/types";
import { AccountSwitcherOptionGroupDesktop } from "./AccountSwitcherOptionGroup";

interface AccountSwitcherDropdownAdminDesktopProps {
  workerDetails: UserDetails;
  workplaceDetails: UserDetails;
  onUserClicked: (user: AccountSwitcherUser) => void;
  hasSearchTerm: boolean;
  selectedTab: AdminSearchTabValue;
  onTabChange: (tab: AdminSearchTabValue) => void;
}

export function AccountSwitcherDropdownAdminDesktop(
  props: AccountSwitcherDropdownAdminDesktopProps
) {
  const {
    workerDetails,
    workplaceDetails,
    onUserClicked,
    hasSearchTerm,
    selectedTab,
    onTabChange,
  } = props;
  const isRebrand = useIsRebrand();

  if (!hasSearchTerm) {
    return (
      <Box sx={{ padding: 4, textAlign: "center" }}>
        <Text
          variant="body2"
          sx={(theme) => ({
            color: isRebrand ? theme.palette.base?.muted : theme.palette.navbar?.itemContent,
          })}
        >
          {ADMIN_SEARCH_PLACEHOLDER}
        </Text>
      </Box>
    );
  }

  const showAgents = selectedTab === "agents" || selectedTab === "all";
  const showWorkplaces = selectedTab === "workplaces" || selectedTab === "all";
  const isAllMode = selectedTab === "all";
  const inactiveTabVariant = isRebrand ? "outlined" : "outlineInverted";

  return (
    <Stack sx={{ paddingY: 2, height: "400px" }}>
      {/* Tabs */}
      <Stack direction="row" gap={1} justifyContent="center" sx={{ paddingX: 2, paddingY: 3 }}>
        {ADMIN_SEARCH_TAB_OPTIONS.map((tab) => (
          <Button
            key={tab}
            size="small"
            variant={selectedTab === tab ? "primary" : inactiveTabVariant}
            onClick={() => onTabChange(tab)}
            sx={(theme) => ({
              textTransform: "capitalize",
              minWidth: "80px",
              ...(isRebrand &&
                selectedTab === tab && {
                  backgroundColor: theme.palette.base?.strong,
                  color: theme.palette.base?.inverted?.base,
                  "&:hover": {
                    backgroundColor: theme.palette.base?.strong,
                  },
                }),
            })}
          >
            {ADMIN_SEARCH_TAB_LABELS[tab]}
          </Button>
        ))}
      </Stack>

      {/* Content - Side by side for "both", single column otherwise */}
      {isAllMode ? (
        <Stack
          direction="row"
          sx={{
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box
            data-testid="account-switcher-agents"
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Text
              variant="body2"
              sx={(theme) => ({
                color: isRebrand
                  ? theme.palette.base?.strong
                  : theme.palette.navbar?.itemContentSelected,
                paddingX: 4,
                paddingY: 1,
                fontWeight: 600,
                flexShrink: 0,
                textAlign: "center",
              })}
            >
              AGENTS
            </Text>
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <AccountSwitcherOptionGroupDesktop
                userDetails={workerDetails}
                onUserClicked={onUserClicked}
                loadMoreLabel="Load more agents"
              />
            </Box>
          </Box>
          <Divider
            orientation="vertical"
            flexItem
            sx={(theme) => ({
              borderColor: theme.palette.navbar?.itemContent,
              opacity: 0.4,
            })}
          />
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <Text
              variant="body2"
              sx={(theme) => ({
                color: isRebrand
                  ? theme.palette.base?.strong
                  : theme.palette.navbar?.itemContentSelected,
                paddingX: 4,
                paddingY: 1,
                fontWeight: 600,
                flexShrink: 0,
                textAlign: "center",
              })}
            >
              WORKPLACES
            </Text>
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <AccountSwitcherOptionGroupDesktop
                userDetails={workplaceDetails}
                onUserClicked={onUserClicked}
                loadMoreLabel="Load more workplaces"
              />
            </Box>
          </Box>
        </Stack>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {showAgents && (
            <Box
              data-testid="account-switcher-agents"
              sx={{
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
                paddingX: 1,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <AccountSwitcherOptionGroupDesktop
                userDetails={workerDetails}
                onUserClicked={onUserClicked}
                loadMoreLabel="Load more agents"
              />
            </Box>
          )}

          {showWorkplaces && (
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
                paddingX: 1,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <AccountSwitcherOptionGroupDesktop
                userDetails={workplaceDetails}
                onUserClicked={onUserClicked}
                loadMoreLabel="Load more workplaces"
              />
            </Box>
          )}
        </Box>
      )}
    </Stack>
  );
}
